/**
 * Calls to the Functions API. The SWA Free tier cannot link a backend, so the SPA calls the Functions hostname
 * directly (VITE_API_BASE) and the API's CORS allows this origin. Same shape as MOWCTX Home.
 * The SPA only ever sends the API token; Graph is reached through the API (GET /api/directory/users).
 */
import { apiToken } from './auth';
import type { AssignmentDraft, FieldError, OffboardingDraft, OnboardingDraft, PersonDraft, TemplateDraft } from '../../api/src/requests/validation';

const base = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '');
if (!base) throw new Error('VITE_API_BASE must be set at build time');

export const API_BASE = base;

export interface Access {
  email: string;
  roles: string[];
  hasFullAccess: boolean;
  isSuperAdmin: boolean;
  isHR: boolean;
  canAdmin: boolean;
  canForceStart: boolean;
}

export interface Me {
  name: string;
  upn: string;
  groups: string[];
  access: Access;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body: Record<string, unknown> = {},
  ) {
    super(message);
  }
  get fieldErrors(): FieldError[] {
    return Array.isArray(this.body.errors) ? (this.body.errors as FieldError[]) : [];
  }
}

async function call<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const token = await apiToken();
  const headers: Record<string, string> = { authorization: `Bearer ${token}` };
  if (body !== undefined) headers['content-type'] = 'application/json';
  const res = await fetch(`${API_BASE}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  let json: Record<string, unknown> = {};
  try {
    json = (await res.json()) as Record<string, unknown>;
  } catch {
    /* empty or non-JSON body */
  }
  if (!res.ok) throw new ApiError(res.status, (json.message as string | undefined) ?? res.statusText, json);
  return json as T;
}

export const getMe = () => call<Me>('GET', '/api/me');

// ---- requests ---------------------------------------------------------------------------------------------------

export type ListFilter = 'active' | 'completed' | 'all';

export interface ListRow {
  id: number;
  requestType: 'Onboarding' | 'Offboarding';
  personType: string;
  displayName: string;
  department: string;
  effectiveDate: string | null;
  overallStatus: string;
  managerEmail: string;
  createdByEmail: string;
  createdAt: string;
}

export const listRequests = (filter: ListFilter, q: string) =>
  call<{ rows: ListRow[] }>('GET', `/api/requests?${new URLSearchParams({ filter, q }).toString()}`).then((r) => r.rows);

export interface RequestDetail {
  request: {
    id: number;
    requestType: 'Onboarding' | 'Offboarding';
    personType: string;
    displayName: string;
    department: string;
    managerEmail: string;
    createdByEmail: string;
    createdByName: string | null;
    forceStart: boolean;
    overallStatus: string;
    cancelledAt: string | null;
    cancelledBy: string | null;
    createdAt: string;
  };
  onboarding: null | {
    firstName: string;
    lastName: string;
    preferredName: string | null;
    jobTitle: string;
    startDay: string;
    phoneNumber: string;
    newOrTransfer: string;
    officeLocation: string | null;
    roleTemplateTitle: string | null;
    salesforceAccess: boolean;
    salesforceMirrorEmail: string | null;
    teamsPhone: boolean;
    teamsExtensionRequested: string | null;
    teamsExtensionAssigned: string | null;
    hasDirectReports: boolean;
    equipOther: boolean;
    equipOtherDescription: string | null;
    workEmail: string | null;
    /** Phase 3b: set by the onboarding run once the Entra account exists. */
    entraUserId: string | null;
    entraCreatedAt: string | null;
    /** Phase 3c: stamped by the Salesforce stage (user and/or Contact). */
    salesforceUserId: string | null;
    salesforceProvisionedAt: string | null;
    salesforceContactId: string | null;
  };
  offboarding: null | {
    /** The leaver's directory display name as resolved at submit (= request.displayName). */
    employeeName: string;
    employeeEmail: string;
    employeeEntraId: string | null;
    lastDay: string;
    accessTerminationAt: string;
    emailAccessNeeded: boolean;
    emailAccessRecipient: string | null;
    onedriveAccessNeeded: boolean;
    onedriveAccessRecipient: string | null;
    equipOther: boolean;
    equipOtherDescription: string | null;
    /** Phase 3b: set by the offboarding run once the account was disabled. */
    accountTerminatedAt: string | null;
    /** Phase 3c: stamped by the Salesforce offboarding step. */
    salesforceDeactivatedAt: string | null;
    salesforceRoutesEndDated: number | null;
  };
  sharepointSites: { siteId: string; title: string }[];
  distroLists: { groupId: string; displayName: string; mail: string | null; groupType: string; onPremSynced: boolean }[];
  sharedMailboxes: { mail: string; label: string }[];
  callQueues: { queueId: string; label: string; resourceAccountUpn: string }[];
  software: string[];
  equipment: string[];
  statuses: { scope: string; status: string }[];
  statusHistory: { scope: string; oldStatus: string | null; newStatus: string; changedBy: string; reason: string | null; changedAt: string }[];
  /** Phase 3a: derived from the row on the server (Cancelled | With issues | Complete | Holding | Running…);
   *  holdEndsAt is the hold deadline while Holding, null afterwards. */
  automation: { state: string; openErrors: number; plannerTasksCreatedAt: string | null; holdEndsAt: string | null };
  cancel: { canCancel: boolean; cancelWindowEndsAt: string; pastWindow: boolean };
  /** Phase 3c (defaults 13, 17): what the Salesforce stage did, from the run output (null before the stage ran). */
  salesforce: SalesforceView | null;
}

export type SalesforceKind =
  | 'created'
  | 'exists'
  | 'contact-only'
  | 'blocked'
  | 'mirror-missing'
  | 'failed'
  | 'skipped'
  | 'deactivated'
  | 'user-not-found'
  | 'deactivation-failed';

export interface SalesforceOutcome {
  kind: SalesforceKind;
  userId?: string;
  username?: string;
  contactId?: string;
  contactAction?: 'created' | 'patched' | 'multiple';
  permissionSetsAssigned?: string[];
  permissionSetsFailed?: string[];
  geopointe?: 'assigned' | 'blocked' | 'not-requested' | 'already' | 'no-package';
  licence?: { name: string; used: number; total: number };
  mirror?: { email: string; username: string; profile: string };
  routesEndDated?: number;
  packageLicensesRemoved?: number;
  failedStep?: string;
  reason?: string;
}

export interface SalesforceView {
  outcome: SalesforceOutcome | null;
  from: 'run' | 'retry' | null;
  retry: { instanceId: string; runtimeStatus: string | null; customStatus: unknown; lastUpdatedTime: string | null };
  busy: boolean;
  canRetry: boolean;
}

/** Phase 3c default 13, IT or Super Admin: provision the hire in Salesforce again. 403 | 404 | 409 (busy, cancelled, not onboarding). */
export const retrySalesforce = (id: number) =>
  call<{ id: number; instanceId: string; action: 'started' }>('POST', `/api/requests/${id}/salesforce/retry`, {});

export const getRequest = (id: number) => call<RequestDetail>('GET', `/api/requests/${id}`);

export const createOnboarding = (draft: OnboardingDraft) =>
  call<{ id: number; displayName: string }>('POST', '/api/requests/onboarding', draft);

export const createOffboarding = (draft: OffboardingDraft) =>
  call<{ id: number; displayName: string }>('POST', '/api/requests/offboarding', draft);

export const cancelRequest = (id: number) =>
  call<{ id: number; cancelledAt: string; cancelledBy: string; alreadyCancelled: boolean; eventRaised: boolean }>('POST', `/api/requests/${id}/cancel`, {});

/** Phase 3a, Super Admin only: skip the rest of the 72-hour hold. 403 | 404 | 409 (cancelled, past-hold, already-ran). */
export const forceStartRequest = (id: number) =>
  call<{ id: number; instanceId: string; action: 'event-raised' | 'started' }>('POST', `/api/requests/${id}/forcestart`, {});

/** Phase 3a, Super Admin only: the run's live state from the task hub (`exists: false` when nothing has run). */
export interface RunStatus {
  exists: boolean;
  instanceId: string;
  name?: string;
  runtimeStatus?: string;
  customStatus?: unknown;
  createdTime?: string | null;
  lastUpdatedTime?: string | null;
  output?: unknown;
}
export const getOrchestration = (id: number) => call<RunStatus>('GET', `/api/requests/${id}/orchestration`);

// ---- form data --------------------------------------------------------------------------------------------------

export interface DirectoryUser {
  id: string;
  displayName: string;
  email: string;
  jobTitle: string | null;
  department: string | null;
}

export const searchDirectory = (q: string) =>
  call<{ rows: DirectoryUser[] }>('GET', `/api/directory/users?${new URLSearchParams({ q }).toString()}`).then((r) => r.rows);

/** One device Intune holds for a user (GET /api/directory/users/{id|upn}/devices). The endpoint's proposed
 *  `equipment` value is carried but never shown or used: the list is reporting, not form logic (Marjorie, 2026-10-15). */
export interface ManagedDevice {
  id: string;
  deviceName: string;
  operatingSystem: string | null;
  osVersion: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  managementState: string | null;
  lastSyncDateTime: string | null;
  equipment: string | null;
}

/** 200 with rows (possibly none); throws ApiError 400 / 404 (unknown user) / 502 (Graph unavailable). */
export const getUserDevices = (key: string) =>
  call<{ user: string; count: number; rows: ManagedDevice[] }>('GET', `/api/directory/users/${encodeURIComponent(key)}/devices`);

export interface Department {
  name: string;
  sort_order: number;
  active: boolean;
}
export const getDepartments = () => call<{ rows: Department[] }>('GET', '/api/departments').then((r) => r.rows);

export interface RoleTemplate {
  id: number;
  title: string;
  department: string;
  /** '' = the department-wide default (prompt default 7). */
  job_title: string;
  salesforce_access: boolean;
  salesforce_mirror_email: string | null;
  teams_phone: boolean;
  has_direct_reports: boolean;
  credit_card: boolean;
  gas_pin: boolean;
  key_fob: boolean;
  cell_phone: boolean;
  laptop: boolean;
  cubicle_keys: boolean;
  notes: string | null;
  updated_at: string;
  software: string[];
  call_queues: { label: string; address: string }[];
  sharepoint_sites: { site_id: string; title: string }[];
}
export const getRoleTemplates = () => call<{ rows: RoleTemplate[] }>('GET', '/api/role-templates').then((r) => r.rows);

// ---- admin tabs (Phase 2d; every write is Super Admin / HR only, the server decides) ------------------------------

export interface RoleAccessRow {
  id: number;
  user_email: string;
  user_name: string | null;
  role: string;
  active: boolean;
  added_by: string | null;
  added_at: string;
  deactivated_at: string | null;
  deactivated_by: string | null;
  notes: string | null;
}
export const getRoleAccess = () => call<{ rows: RoleAccessRow[] }>('GET', '/api/role-access').then((r) => r.rows);

/** The server's view of one person after a write (admin/store.ts Person). */
export interface Person {
  email: string;
  userName: string;
  notes: string | null;
  active: boolean;
  roles: string[];
  rows: RoleAccessRow[];
}
const personPath = (email: string) => `/api/role-access/people/${encodeURIComponent(email)}`;
export const savePerson = (email: string, draft: PersonDraft) => call<Person>('PUT', personPath(email), draft);
export const deactivatePerson = (email: string) => call<Person>('POST', `${personPath(email)}/deactivate`, {});
export const reactivatePerson = (email: string) => call<Person>('POST', `${personPath(email)}/reactivate`, {});

export const createRoleTemplate = (draft: TemplateDraft) => call<{ id: number; title: string }>('POST', '/api/role-templates', draft);
export const updateRoleTemplate = (id: number, draft: TemplateDraft) => call<{ id: number; title: string }>('PUT', `/api/role-templates/${id}`, draft);
export const deleteRoleTemplate = (id: number) => call<{ id: number; title: string }>('DELETE', `/api/role-templates/${id}`);

export interface Assignment {
  id: number;
  title: string;
  category: string;
  task_role: string;
  updated_at: string;
  /** rules.ts looks this title up; it cannot be renamed or deleted. */
  protected: boolean;
}
export const getAssignments = () => call<{ rows: Assignment[] }>('GET', '/api/task-role-assignments').then((r) => r.rows);
export const createAssignment = (draft: AssignmentDraft) => call<{ id: number; title: string; protected: boolean }>('POST', '/api/task-role-assignments', draft);
export const updateAssignment = (id: number, draft: AssignmentDraft) =>
  call<{ id: number; title: string; protected: boolean }>('PUT', `/api/task-role-assignments/${id}`, draft);
export const deleteAssignment = (id: number) => call<{ id: number; title: string }>('DELETE', `/api/task-role-assignments/${id}`);

export type PickerKind = 'sharepoint-sites' | 'distro-lists' | 'shared-mailboxes' | 'call-queues';

/** The picker endpoint's rows; `id` and `label` are common, the rest varies by kind. */
export interface PickerRow {
  id: string;
  label: string;
  mail?: string | null;
  site_type?: string;
  group_name?: string | null;
  group_type?: string;
  on_prem_synced?: boolean;
  resource_account_upn?: string;
}
export const getPicker = (kind: PickerKind) => call<{ rows: PickerRow[] }>('GET', `/api/pickers/${kind}`).then((r) => r.rows);
